import React, { useEffect, useState, useCallback } from "react";
import { useNavigation } from "@react-navigation/native";
import type { NativeStackNavigationProp } from "@react-navigation/native-stack";

import AllMeetingsView from "../ui/AllMeetingsView";
import { onAuthStateChanged } from "firebase/auth";
import { doc, onSnapshot } from "firebase/firestore";
import RootStackParamList from "../../../navigation/RootStackParamList";
import { useRooms } from "../../../hooks/useRooms";
import { auth, db } from "../../../firebase";
import { MAX_CAPACITY } from "../../../constants/common";
import { Room } from "../../../types/types";

type NavigationProp = NativeStackNavigationProp<RootStackParamList>;

export default function AllMeetingsContainer() {
  const navigation = useNavigation<NavigationProp>();
  const { rooms, loading, error } = useRooms();
  const [currentUserId, setCurrentUserId] = useState<string | null>(
    auth.currentUser?.uid ?? null,
  );

  const [participantsByChannelId, setParticipantsByChannelId] = useState<
    Record<string, number>
  >({});

  const ROOM_OWNER_COUNT = 1;

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setCurrentUserId(user?.uid ?? null);
    });
    return unsubscribe;
  }, []);

  useEffect(() => {
    if (!rooms?.length) return;

    const unsubscribeList: (() => void)[] = [];
    const seen = new Set<string>();

    rooms.forEach((room: Room) => {
      const channelId = room.id;
      if (!channelId || seen.has(channelId)) return;
      seen.add(channelId);

      const roomRef = doc(db, "rooms", channelId);
      const unsubscribe = onSnapshot(
        roomRef,
        (snapshot) => {
          if (!snapshot.exists()) return;

          const data = snapshot.data() as any;
          const participantsField = data?.participants;

          const count = Array.isArray(participantsField)
            ? participantsField.length
            : 0;

          setParticipantsByChannelId((prev) => {
            if (prev[channelId] === count) return prev;
            return { ...prev, [channelId]: count };
          });
        },
        (err) => {
          console.error("Room subscription error:", channelId, err);
        },
      );

      unsubscribeList.push(unsubscribe);
    });

    return () => {
      unsubscribeList.forEach((off) => off());
    };
  }, [rooms]);

  const errorText =
    (error as any)?.message ??
    (typeof error === "string" ? error : null) ??
    null;

  const getParticipantsCount = useCallback(
    (room: Room) => {
      const channelId = room.id;
      const liveParticipants = participantsByChannelId[channelId];
      const roomParticipants = room.participants?.length ?? 0;

      return (liveParticipants ?? roomParticipants) + ROOM_OWNER_COUNT;
    },
    [participantsByChannelId],
  );

  const isRoomDisabled = useCallback(
    (room: Room) =>
      getParticipantsCount(room) >= (room.maxParticipants ?? MAX_CAPACITY) ||
      room.createdBy === currentUserId ||
      (!!currentUserId && !!room.participants?.includes(currentUserId)),
    [currentUserId, getParticipantsCount],
  );

  const getDisabledActionLabel = useCallback(
    (room: Room) => {
      if (room.createdBy === currentUserId) return "YOUR CONVERSATION";
      if (currentUserId && room.participants?.includes(currentUserId)) {
        return "JOINED";
      }
      return undefined;
    },
    [currentUserId],
  );

  const onRoomPress = useCallback(
    (room: Room) => {
      if (isRoomDisabled(room)) return;
      navigation.navigate("JoinConfirm", { room });
    },
    [navigation, isRoomDisabled],
  );

  const onPressSchedule = useCallback(() => {
    navigation.navigate("Schedule");
  }, [navigation]);

  return (
    <AllMeetingsView
      rooms={rooms}
      loading={loading}
      errorText={errorText}
      getParticipantsCount={getParticipantsCount}
      isRoomDisabled={isRoomDisabled}
      getDisabledActionLabel={getDisabledActionLabel}
      onRoomPress={onRoomPress}
      onPressSchedule={onPressSchedule}
    />
  );
}
